import { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import { Shortcut } from '../types';

export function useShortcuts(userId: string | null) {
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setShortcuts([]);
      setLoading(false);
      return;
    }

    console.log('Fetching shortcuts for user:', userId);
    setLoading(true);
    setError(null);

    // Note: In a medical organization, all shortcuts should be shared across the organization
    // This is intentional - all @gangerdermatology.com users see all shortcuts
    // Get all active shortcuts (Chrome extension filters by isActive)
    // Simplified query to avoid composite index requirement
    const q = query(
      collection(db, 'shortcuts'), 
      orderBy('shortcut')
    );
    
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs
          .map(doc => {
            const docData = doc.data();
            return {
              id: doc.id,
              shortcut: docData.shortcut || '',
              expansion: docData.expansion || '',
              createdBy: docData.createdBy || docData.userId || '',
              createdAt: docData.createdAt?.toDate() || new Date(),
              userId: docData.userId || '',
              isTemplate: docData.isTemplate || false,
              isActive: docData.isActive !== false // Default to true if not specified
            } as Shortcut;
          })
          .filter(shortcut => shortcut.isActive); // Filter out inactive shortcuts client-side
        setShortcuts(data);
        setLoading(false);
      },
      (error: any) => {
        console.error('Error fetching shortcuts:', error);
        // Provide more detailed error message
        let errorMessage = 'Failed to load shortcuts';
        if (error.code === 'failed-precondition') {
          errorMessage = 'Database index required. Please check Firebase Console for missing indexes.';
        } else if (error.code === 'permission-denied') {
          errorMessage = 'Permission denied. Please check Firestore security rules.';
        } else if (error.message) {
          errorMessage = `Failed to load shortcuts: ${error.message}`;
        }
        setError(errorMessage);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const createShortcut = async (shortcut: string, expansion: string, isTemplate = false) => {
    if (!userId) throw new Error('User not authenticated');
    
    try {
      const userEmail = auth.currentUser?.email || '';
      await addDoc(collection(db, 'shortcuts'), {
        shortcut: shortcut.toLowerCase(), // Chrome extension expects lowercase
        expansion,
        isActive: true,
        createdAt: new Date(),
        createdBy: userEmail,
        updatedAt: new Date(),
        updatedBy: userEmail,
        userId,
        isTemplate
      });
    } catch (error) {
      console.error('Error creating shortcut:', error);
      throw error;
    }
  };

  const updateShortcut = async (id: string, expansion: string) => {
    try {
      const userEmail = auth.currentUser?.email || '';
      await updateDoc(doc(db, 'shortcuts', id), { 
        expansion,
        updatedAt: new Date(),
        updatedBy: userEmail
      });
    } catch (error) {
      console.error('Error updating shortcut:', error);
      throw error;
    }
  };

  const deleteShortcut = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'shortcuts', id));
    } catch (error) {
      console.error('Error deleting shortcut:', error);
      throw error;
    }
  };

  return { shortcuts, loading, error, createShortcut, updateShortcut, deleteShortcut };
}